import React, { useState, useEffect, useRef, useMemo } from "react";
import { Modal, Button, Form, Dropdown } from "react-bootstrap";
import swal from "sweetalert";
import axios from "axios";
import BASE_URL from "../../../../assets/global/url";
import { useNavigate } from "react-router-dom";
import { ThreeDot } from "react-loading-indicators";
import NoAccess from "../../../../assets/img/NoAccess.png";
import useDecodeToken from "../../../../hooks/customHook/useDecodeToken";
import DatePicker from "react-datepicker";
import { differenceInCalendarDays } from "date-fns";
import CustomDatePicker from "../../../../components/CustomDatePicker";
import { useServerPagination } from "../../../../hooks/customHook/paginationHook/useServerPagination";
import { PaginationControls } from "../../../../hooks/customHook/paginationHook/usePagination";
import FilterStore from "../../../../hooks/customHook/FilterStore";
import { MUTED, ActionsToggle, PeriodStrip, cardStyle } from "./CardParts";

function CutoffContent({ authrztn, viewSwitch }) {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  const [toUpdateID, setToUpdateID] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [cutoff_name, setCutoff_name] = useState("");
  const [validated, setValidated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCutoffs, setSelectedCutoffs] = useState([]);
  const [showModalEarningsName, setShowModalEarningsName] = useState(false);
  const [earningsName, setEarningsName] = useState("");
  const [searchText, setSearchText] = useState(() => FilterStore.COgetSearch());
  useEffect(() => {
    FilterStore.COsaveSearch(searchText);
  }, [searchText]);
  const rowItems = useRef([]);

  const userLoggedID = useDecodeToken();
  const pagination = useServerPagination(
    "about:blank",
    FilterStore.COgetPerPage(),
  );

  const pageRestored = useRef(false);

  // Save page to FilterStore whenever it changes.
  // Gate is closed until after mount restore so the hook's internal
  // reset-to-1 doesn't overwrite the saved page.
  useEffect(() => {
    if (!pageRestored.current) return;
    if (pagination.currentPage) {
      FilterStore.COsavePage(pagination.currentPage);
    }
  }, [pagination.currentPage]);

  // Save perPage to FilterStore whenever it changes
  // NOTE: hook exposes `itemsPerPage` + `changeItemsPerPage`, not `rowsPerPage`
  useEffect(() => {
    if (pagination.itemsPerPage) {
      FilterStore.COsavePerPage(pagination.itemsPerPage);
    }
  }, [pagination.itemsPerPage]);

  const handleClose = () => {
    setShow(false);
    setValidated(false);
    setFrom("");
    setTo("");
    setCutoff_name("");
    setShowUpdate(false);
    setShowModalEarningsName(false);
    setEarningsName("");
  };
  const handleShow = () => setShow(true);
  const handleShowUpdate = (id, from, to, name) => {
    setShowUpdate(true);
    setToUpdateID(id);
    setFrom(from);
    setTo(to);
    setCutoff_name(name);
  };

  const handleViewCutoff = (id) => {
    navigate(`/accounting/view-cutoff/${id}`);
  };

  const reloadTable = () => {
    pagination.updateApiUrl(`${BASE_URL}/cutoff/getCutOffsForFilter`);
    pagination.updateParams({
      searchText,
    });
    setIsLoading(false);
  };

  useEffect(() => {
    reloadTable();
    // updateApiUrl() inside reloadTable calls setCurrentPage(1) internally.
    // Defer the restore so it runs after that reset settles, then open the
    // save gate so user navigation is persisted from here on.
    const savedPage = FilterStore.COgetPage();
    if (savedPage > 1) {
      setTimeout(() => {
        pagination.setCurrentPage(savedPage);
        pageRestored.current = true;
      }, 0);
    } else {
      setTimeout(() => {
        pageRestored.current = true;
      }, 0);
    }
  }, []);

  const isFirstFilterReset = useRef(true);

  useEffect(() => {
    if (isFirstFilterReset.current) {
      isFirstFilterReset.current = false;
      return;
    }
    // Reset to page 1 whenever search changes
    pagination.setCurrentPage(1);
    FilterStore.COsavePage(1);
  }, [searchText]);

  useEffect(() => {
    function handleClickOutside(event) {
      const clickAnyOutside = rowItems.current.some(
        (ref) => ref && ref.contains(event.target),
      );

      const otherIncludedAreas = event.target.closest(
        ".react-date-picker, .add-buttons-container, .retained-earnings-modal, .cutoff-modal",
      );

      if (!clickAnyOutside && !otherIncludedAreas) {
        setSelectedCutoffs([]);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePostCutoff = (id) => {
    swal({
      title: "Confirm Action",
      text: "Are you sure you want to post this cutoff period?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((confirmed) => {
      if (confirmed) {
        try {
          axios
            .post(`${BASE_URL}/cutoff/postCutoff`, {
              params: { id, userLoggedID },
            })
            .then((res) => {
              if (res.status === 200) {
                swal({
                  icon: "success",
                  title: "Success",
                  text: "Cutoff posted successfully",
                  timer: 2000,
                  button: false,
                }).then(() => {
                  handleClose();
                  reloadTable();
                });
              } else {
                swal({
                  icon: "error",
                  title: "Something went wrong",
                  text: "Please contact your support immediately",
                  timer: 2000,
                  button: false,
                });
              }
            });
        } catch (error) {
          console.log(error);
        }
      }
    });
  };

  const addNew = (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();
      swal({
        icon: "error",
        title: "Fields are required",
        text: "Please fill in the red text fields.",
      });
    } else {
      swal({
        title: "Confirm Action",
        text: "Are you sure you want to create a new cutoff period?",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      }).then((confirmed) => {
        if (confirmed) {
          try {
            axios
              .post(`${BASE_URL}/cutoff/createCutoff`, {
                init_from: from,
                init_to: to,
                cutoff_name,
                userLoggedID,
              })
              .then((res) => {
                if (res.status === 200) {
                  swal({
                    icon: "success",
                    title: "Success",
                    text: "Cutoff created successfully",
                    timer: 2000,
                    button: false,
                  }).then(() => {
                    handleClose();
                    reloadTable();
                  });
                } else if (res.status === 201) {
                  swal({
                    icon: "error",
                    title: "Oppss!",
                    text: "Date range overlaps with existing cutoff period",
                    timer: 2000,
                    button: false,
                  });
                } else {
                  swal({
                    icon: "error",
                    title: "Something went wrong",
                    text: "Please contact your support immediately",
                    timer: 2000,
                    button: false,
                  });
                }
              })
              .catch((error) => {
                if (error.response && error.response.status === 409) {
                  swal({
                    icon: "error",
                    title: "New Cutoff Creation Failed!",
                    text: error.response.data.message,
                    button: "OK",
                  });
                  return;
                }

                if (error.response && error.response.status === 404) {
                  swal({
                    icon: "error",
                    title: "New Cutoff Creation Failed!",
                    text: error.response.data.message,
                    button: "OK",
                  });
                  return;
                }
              });
          } catch (error) {
            console.log(error);
            swal({
              icon: "error",
              title: "Something went wrong",
              text: "Please contact your support immediately",
              timer: 2000,
              button: false,
            });
          }
        }
      });
    }
    setValidated(true);
  };

  const updateCutoff = (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();
      swal({
        icon: "error",
        title: "Fields are required",
        text: "Please fill in the red text fields.",
      });
    } else {
      swal({
        title: "Confirm Action",
        text: "Are you sure you want to update this cutoff period?",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      }).then((confirmed) => {
        if (confirmed) {
          try {
            axios
              .post(`${BASE_URL}/cutoff/updateCutoff`, {
                id: toUpdateID,
                from,
                to,
                cutoff_name,
                userLoggedID,
              })
              .then((res) => {
                if (res.status === 200) {
                  swal({
                    icon: "success",
                    title: "Success",
                    text: "Cutoff updated successfully",
                    timer: 2000,
                    button: false,
                  }).then(() => {
                    handleClose();
                    reloadTable();
                  });
                } else if (res.status === 201) {
                  swal({
                    icon: "error",
                    title: "Oppss!",
                    text: "Date range overlaps with existing cutoff period",
                    timer: 2000,
                    button: false,
                  });
                } else {
                  swal({
                    icon: "error",
                    title: "Something went wrong",
                    text: "Please contact your support immediately",
                    timer: 2000,
                    button: false,
                  });
                }
              });
          } catch (error) {
            console.log(error);
            swal({
              icon: "error",
              title: "Something went wrong",
              text: "Please contact your support immediately",
              timer: 2000,
              button: false,
            });
          }
        }
      });
    }
    setValidated(true);
  };

  const handleDeleteCutoff = (id, isPosted) => {
    swal({
      title: "Confirm Action",
      text: isPosted
        ? "You are about to delete a posted cutoff. This will unlock the transactions for the period and may impact accounting reports. Are you sure?"
        : "Are you sure you want to delete this cutoff period?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((confirmed) => {
      if (confirmed) {
        try {
          axios
            .post(`${BASE_URL}/cutoff/deleteCutoff`, {
              params: { id, userLoggedID },
            })
            .then((res) => {
              if (res.status === 200) {
                swal({
                  icon: "success",
                  title: "Success",
                  text: "Cutoff deleted successfully",
                  timer: 2000,
                  button: false,
                }).then(() => {
                  handleClose();
                  reloadTable();
                });
              } else if (res.status === 201) {
                const inRetained = res.data.source === "retained_net_income";
                swal({
                  icon: "error",
                  title: inRetained
                    ? "Cutoff is used in Retained Net Income"
                    : "Cutoff is used in Earnings",
                  text: inRetained
                    ? `Please remove this cutoff from the retained net income named "${res.data.message}" first`
                    : `Please delete the earnings first named "${res.data.message}"`,
                  timer: 2000,
                  button: false,
                });
              } else {
                swal({
                  icon: "error",
                  title: "Something went wrong",
                  text: "Please contact your support immediately",
                  timer: 2000,
                  button: false,
                });
              }
            })
            .catch((error) => {
              console.log(error);
              swal({
                icon: "error",
                title: "Something went wrong",
                text: "Please contact your support immediately",
                timer: 2000,
                button: false,
              });
            });
        } catch (error) {
          console.log(error);
          swal({
            icon: "error",
            title: "Something went wrong",
            text: "Please contact your support immediately",
            timer: 2000,
            button: false,
          });
        }
      }
    });
  };

  const handleCutoffSelection = (
    cutoffId,
    cutoffName,
    cutoffFrom,
    cutoffTo,
  ) => {
    setSelectedCutoffs((prev) => {
      if (prev.some((selected) => selected.value === cutoffId)) {
        return prev.filter((selected) => selected.value !== cutoffId);
      } else {
        return [
          ...prev,
          {
            value: cutoffId,
            label: cutoffName,
            from: cutoffFrom,
            to: cutoffTo,
          },
        ];
      }
    });
  };

  const handleAddRetainedEarnings = (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();
      swal({
        icon: "error",
        title: "Fields are required",
        text: "Please fill in the red text fields.",
      });
    } else {
      swal({
        title: "Confirm Action",
        text: "Are you sure you want to add this cutoff/s to Profit Distribution?",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      }).then((confirmed) => {
        if (confirmed) {
          axios
            .post(`${BASE_URL}/earnings/createEarnings`, {
              earning_name: earningsName,
              selectedOptionsCutoff: selectedCutoffs,
              userLoggedID,
            })
            .then((res) => {
              if (res.status === 200) {
                swal({
                  icon: "success",
                  title: "Success",
                  text: "Earnings created successfully",
                  timer: 2000,
                  button: false,
                }).then(() => {
                  handleClose();
                  navigate("/accounting/profit-distribution");
                });
              } else if (res.status === 201) {
                swal({
                  icon: "error",
                  title: "Oppss!",
                  text: "Date range overlaps with existing earnings period",
                  timer: 2000,
                  button: false,
                });
              } else {
                swal({
                  icon: "error",
                  title: "Something went wrong",
                  text: "Please contact your support immediately",
                  timer: 2000,
                  button: false,
                });
              }
            });
        }
      });
    }
  };

  const debounceTimer = useRef(null);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchText(value);

    // Clear previous debounce timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new debounce timer
    debounceTimer.current = setTimeout(async () => {
      pagination.updateApiUrl(`${BASE_URL}/cutoff/getCutOffsForFilter`);
      pagination.updateParams({
        searchText: value,
      });
    }, 600);

    // Cleanup timer on unmount or searchTermFilter change
    return () => clearTimeout(debounceTimer.current);
  };

  // Latest From date. It is read at click/validation time, so the inputs below can
  // stay stable (not re-created every time From changes).
  const fromRef = useRef(from);
  useEffect(() => {
    fromRef.current = from;
  }, [from]);

  // Custom inputs for DatePicker: the user can type the date (MM/dd/yyyy) or pick it
  // from the calendar (same behavior as the Counting Date). Wrapped in useMemo so the
  // component types stay stable (the input isn't remounted / doesn't lose focus on
  // every keystroke) and they use local state so react-datepicker can't override the
  // user's typing/backspaces. Only a COMPLETE date (MM/dd/yyyy) is handed over to
  // react-datepicker, so a half-typed date (e.g. 10/09/2) never reaches the From/To
  // state that is sent to createCutoff/updateCutoff.
  // CustomInput is used by the From pickers; CustomInputTo by the To pickers, which
  // also validate the date as soon as it is completely typed.
  const [CustomInput, CustomInputTo] = useMemo(() => {
    // validateTyped(text) runs once a COMPLETE date is typed. It returns false
    // (after warning the user) when the typed date must be rejected.
    const createInput = (validateTyped) => {
      const Input = React.forwardRef(
        (
          {
            value,
            onClick,
            onChange,
            onBlur,
            onFocus,
            onKeyDown,
            generateYears,
          },
          ref,
        ) => {
          // Local state allows free typing/backspacing without strict overrides
          const [localValue, setLocalValue] = useState(value || "");
          const [isFocused, setIsFocused] = useState(false);

          // Sync localValue with react-datepicker only when NOT focused. On blur this
          // also puts back the last valid date if the typed one was incomplete/rejected.
          useEffect(() => {
            if (!isFocused) {
              setLocalValue(value || "");
            }
          }, [value, isFocused]);

          const handleChange = (e) => {
            const raw = e?.target?.value || "";

            // Remove any characters that aren't digits or slashes
            const cleaned = raw.replace(/[^\d/]/g, "");

            // Auto-format MM/dd/yyyy
            const next = cleaned
              .replace(/^(\d{2})(\d)/, "$1/$2")
              .replace(/^(\d{2}\/\d{2})(\d)/, "$1/$2");

            // Validate length up to MM/dd/yyyy format
            if (/^(\d{0,2}|\d{2}\/\d{0,2}|\d{2}\/\d{2}\/\d{0,4})$/.test(next)) {
              setLocalValue(next); // Update the visual input instantly

              // Only a complete date is validated and handed over to react-datepicker
              if (next.length === 10) {
                if (validateTyped && !validateTyped(next)) {
                  setLocalValue(value || ""); // Put back the last valid date
                  return;
                }

                if (onChange) {
                  e.target.value = next;
                  onChange(e);
                }
              }
            }
          };

          const handleFocus = (e) => {
            setIsFocused(true);
            if (onFocus) onFocus(e);
          };

          const handleBlur = (e) => {
            setIsFocused(false);
            if (onBlur) onBlur(e);
          };

          return (
            <input
              type="text"
              className="form-control p-2 w-100"
              style={{ cursor: "text" }}
              onClick={(e) => {
                if (onClick) onClick(e);

                let selectedYear = new Date().getFullYear();
                const base = value || fromRef.current;
                if (base) {
                  const parsed = new Date(base);
                  // Extract year safely if the date is valid
                  if (!isNaN(parsed.getTime())) {
                    selectedYear = parsed.getFullYear();
                  }
                }

                // Reset/Initialize Year List based on selected date
                if (generateYears && typeof generateYears === "function") {
                  generateYears(selectedYear);
                }
              }}
              onChange={handleChange}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onKeyDown={onKeyDown}
              value={localValue}
              ref={ref}
              placeholder="MM/DD/YYYY"
              required
            />
          );
        },
      );

      Input.displayName = "CustomInput";
      return Input;
    };

    // To: the typed date must be a real date and not earlier than From
    const validateToDate = (text) => {
      const [mm, dd, yyyy] = text.split("/").map(Number);
      const typed = new Date(yyyy, mm - 1, dd);

      if (
        typed.getFullYear() !== yyyy ||
        typed.getMonth() !== mm - 1 ||
        typed.getDate() !== dd
      ) {
        swal({
          icon: "warning",
          title: "Invalid Date",
          text: "Please enter a valid To date (MM/dd/yyyy).",
          buttons: false,
          timer: 2500,
        });
        return false;
      }

      const fromDate = fromRef.current ? new Date(fromRef.current) : null;
      if (fromDate && !isNaN(fromDate.getTime())) {
        fromDate.setHours(0, 0, 0, 0);

        if (typed < fromDate) {
          swal({
            icon: "warning",
            title: "Invalid To Date",
            text: "To date cannot be earlier than the From date.",
            buttons: false,
            timer: 2500,
          });
          return false;
        }
      }

      return true;
    };

    return [createInput(), createInput(validateToDate)];
  }, []);

  return (
    <div className="h-100 w-100 border bg-white custom-container">
      {isLoading ? (
        <div className="loading-container">
          <ThreeDot
            variant="brick-stack"
            color="#6290FE"
            size="large"
            text="Loading Data..."
            textColor=""
          />
        </div>
      ) : authrztn.includes("Monthly-View") ? (
        <>
          <div className="w-100 p-2 mb-1 d-flex flex-row justify-content-between">
            <div className="d-flex flex-column title-custom">
              <span className="fs-3 text-uppercase">CutOff</span>
            </div>
            <div className="add-buttons-container">
              {authrztn.includes("Monthly-Add") &&
                (selectedCutoffs.length > 0 ? (
                  <React.Fragment>
                    <Button
                      variant="warning"
                      onClick={() => setShowModalEarningsName(true)}>
                      Add to Profit Distribution
                    </Button>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <Button
                      className="btn d-flex align-items-center title-button"
                      variant="primary"
                      onClick={handleShow}>
                      <i className="bx bx-plus fs-5"></i> Add Cutoff
                    </Button>
                  </React.Fragment>
                ))}
            </div>
          </div>

          {viewSwitch}

          <div className="w-100 pt-2 px-4">
            <input
              type="text"
              className="form-control"
              placeholder="Search"
              value={searchText}
              onChange={handleSearch}
            />
          </div>

          <div className="w-100 p-4">
            <div className="row g-4">
              {pagination.data.map((cutoff, index) => (
                <div className="col-md-4" key={cutoff.id}>
                  <div
                    className="card border-0 rounded-4 h-100 hover-lift transition-shadow"
                    ref={(el) => (rowItems.current[index] = el)}
                    // onClick={() =>
                    //   cutoff.isPosted &&
                    //   cutoff.return_earnings_cutoffs.length === 0 &&
                    //   authrztn.includes("Retained-Add") &&
                    //   handleCutoffSelection(
                    //     cutoff.id,
                    //     cutoff.name,
                    //     cutoff.from,
                    //     cutoff.to,
                    //   )
                    // }
                    style={cardStyle({
                      selected: selectedCutoffs.some(
                        (selected) => selected.value === cutoff.id,
                      ),
                      cursor:
                        cutoff.isPosted &&
                        cutoff.return_earnings_cutoffs.length === 0 &&
                        authrztn.includes("Retained-Add")
                          ? "pointer"
                          : "not-allowed",
                    })}
                    title={
                      !cutoff.isPosted
                        ? "Not Allowed - Cutoff must be posted first"
                        : !(
                              cutoff.isPosted &&
                              cutoff.return_earnings_cutoffs.length === 0
                            )
                          ? "Not Allowed - Cutoff is already in Profit Distribution"
                          : !authrztn.includes("Retained-Add")
                            ? "Access denied – you do not have permission to add Profit Distribution."
                            : ""
                    }>
                    <div className="card-body p-4 d-flex flex-column gap-3">
                      {/* Header: name, status, actions */}
                      <div className="d-flex justify-content-between align-items-start">
                        <div style={{ minWidth: 0 }}>
                          <div className="fw-semibold text-dark fs-5 text-truncate mb-1">
                            {cutoff.name}
                          </div>
                          <div className="d-flex align-items-center gap-2 small">
                            <span
                              className="d-inline-flex align-items-center gap-1 rounded-pill px-2 py-1 fw-medium"
                              style={
                                cutoff.isPosted
                                  ? {
                                      backgroundColor: "#ecfdf3",
                                      color: "#067647",
                                    }
                                  : {
                                      backgroundColor: "#fef3f2",
                                      color: "#b42318",
                                    }
                              }>
                              <span
                                className="rounded-circle"
                                style={{
                                  width: 6,
                                  height: 6,
                                  backgroundColor: "currentColor",
                                }}></span>
                              {cutoff.isPosted ? "Posted" : "Unposted"}
                            </span>
                            {cutoff.from && cutoff.to && (
                              <span style={{ color: MUTED }}>
                                {differenceInCalendarDays(
                                  new Date(cutoff.to),
                                  new Date(cutoff.from),
                                ) + 1}{" "}
                                days
                              </span>
                            )}
                          </div>
                        </div>
                        <Dropdown
                          align="end"
                          onClick={(e) => e.stopPropagation()}>
                          <Dropdown.Toggle
                            as={ActionsToggle}
                            id={`cutoff-actions-${cutoff.id}`}
                          />

                          <Dropdown.Menu className="shadow-sm border-0 rounded-3">
                            <Dropdown.Item
                              onClick={() => handleViewCutoff(cutoff.id)}
                              className="d-flex align-items-center px-3 py-2">
                              <i className="bx bx-show me-2 fs-5 text-success"></i>
                              <span className="text-body-secondary">View</span>
                            </Dropdown.Item>
                            {authrztn.includes("Monthly-Edit") && (
                              <Dropdown.Item
                                disabled={cutoff.isPosted}
                                style={{ opacity: cutoff.isPosted ? 0.5 : 1 }}
                                onClick={() =>
                                  handleShowUpdate(
                                    cutoff.id,
                                    cutoff.from,
                                    cutoff.to,
                                    cutoff.name,
                                  )
                                }
                                className="d-flex align-items-center px-3 py-2">
                                <i className="bx bx-edit me-2 fs-5 text-warning"></i>

                                <span className="text-body-secondary">
                                  Update
                                </span>
                              </Dropdown.Item>
                            )}
                            {authrztn.includes("Monthly-Approve") && (
                              <Dropdown.Item
                                className="d-flex align-items-center px-3 py-2"
                                disabled={cutoff.isPosted}
                                style={{ opacity: cutoff.isPosted ? 0.5 : 1 }}
                                onClick={(e) => {
                                  if (!cutoff.isPosted) {
                                    handlePostCutoff(cutoff.id);
                                  }
                                }}>
                                <i className="bx bx-check-circle me-2 fs-5 text-primary"></i>
                                <span className="text-body-secondary">
                                  Post Cutoff
                                </span>
                              </Dropdown.Item>
                            )}
                            {authrztn.includes("Monthly-Delete") && (
                              <Dropdown.Divider />
                            )}
                            {authrztn.includes("Monthly-Delete") && (
                              <Dropdown.Item
                                onClick={(e) => {
                                  e.stopPropagation();

                                  if (
                                    selectedCutoffs.some(
                                      (item) => item.value === cutoff.id,
                                    )
                                  ) {
                                    setSelectedCutoffs((prev) => {
                                      return prev.filter(
                                        (item) => item.value !== cutoff.id,
                                      );
                                    });
                                  }

                                  handleDeleteCutoff(
                                    cutoff.id,
                                    cutoff.isPosted,
                                  );
                                }}
                                className="d-flex align-items-center px-3 py-2 text-danger">
                                <i className="bx bx-trash me-2 fs-5"></i>
                                <span>Delete</span>
                              </Dropdown.Item>
                            )}
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>

                      {/* Period strip */}
                      <PeriodStrip from={cutoff.from} to={cutoff.to} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <PaginationControls {...pagination} />
          </div>

          {/* Modals remain the same */}
          <Modal
            show={show}
            backdrop="static"
            keyboard={false}
            onHide={handleClose}
            className="cutoff-modal">
            <Form noValidate validated={validated} onSubmit={addNew}>
              <Modal.Header closeButton>
                <Modal.Title>Add Cutoff</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className="row">
                  <div className="col-sm">
                    <label>Cutoff Name :</label>
                    <input
                      type="text"
                      value={cutoff_name}
                      onChange={(e) => setCutoff_name(e.target.value)}
                      className="form-control"
                      required
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm">
                    <label>From :</label>
                    <div className="react-date-picker">
                      <CustomDatePicker
                        selected={from ? new Date(from) : ""}
                        handleDateChange={(date) => {
                          setFrom(date);
                        }}
                        setter={setFrom}
                        CustomInput={CustomInput}
                        isRequired={true}
                        validated={validated}
                        maxDate={to}
                      />
                    </div>
                  </div>
                  <div className="col-sm">
                    <label>To :</label>
                    <div className="react-date-picker">
                      <CustomDatePicker
                        selected={to ? new Date(to) : ""}
                        handleDateChange={(date) => {
                          setTo(date);
                        }}
                        setter={setTo}
                        CustomInput={CustomInputTo}
                        isRequired={true}
                        validated={validated}
                        minDate={from}
                      />
                    </div>
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" type="button" onClick={handleClose}>
                  Close
                </Button>
                <Button variant="primary" type="submit">
                  Add
                </Button>
              </Modal.Footer>
            </Form>
          </Modal>

          <Modal
            show={showUpdate}
            backdrop="static"
            keyboard={false}
            onHide={handleClose}
            className="cutoff-modal">
            <Form noValidate validated={validated} onSubmit={updateCutoff}>
              <Modal.Header closeButton>
                <Modal.Title>Update Cutoff</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className="row">
                  <div className="col-sm">
                    <label>Cutoff Name :</label>
                    <input
                      type="text"
                      value={cutoff_name}
                      onChange={(e) => setCutoff_name(e.target.value)}
                      className="form-control"
                      required
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm">
                    <label>From :</label>
                    <div className="react-date-picker">
                      <CustomDatePicker
                        selected={from ? new Date(from) : ""}
                        handleDateChange={(date) => {
                          setFrom(date);
                        }}
                        setter={setFrom}
                        CustomInput={CustomInput}
                        isRequired={true}
                        validated={validated}
                        maxDate={to}
                      />
                    </div>
                  </div>
                  <div className="col-sm">
                    <label>To :</label>
                    <div className="react-date-picker">
                      <CustomDatePicker
                        selected={to ? new Date(to) : ""}
                        handleDateChange={(date) => {
                          setTo(date);
                        }}
                        setter={setTo}
                        CustomInput={CustomInputTo}
                        isRequired={true}
                        validated={validated}
                        minDate={from}
                      />
                    </div>
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" type="button" onClick={handleClose}>
                  Close
                </Button>
                <Button variant="primary" type="submit">
                  Update
                </Button>
              </Modal.Footer>
            </Form>
          </Modal>

          <Modal
            show={showModalEarningsName}
            backdrop="static"
            keyboard={false}
            onHide={handleClose}
            className="retained-earnings-modal">
            <Form
              noValidate
              validated={validated}
              onSubmit={handleAddRetainedEarnings}>
              <Modal.Header closeButton>
                <Modal.Title>Please Add Name First</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className="row">
                  <div className="col-sm">
                    <label>Earnings Name :</label>
                    <input
                      type="text"
                      value={earningsName}
                      onChange={(e) => setEarningsName(e.target.value)}
                      className="form-control"
                      required
                    />
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" type="button" onClick={handleClose}>
                  Close
                </Button>
                <Button variant="primary" type="submit">
                  Add
                </Button>
              </Modal.Footer>
            </Form>
          </Modal>
        </>
      ) : (
        <div className="no-access">
          <img src={NoAccess} alt="NoAccess" className="no-access-img" />
          <h3>You don't have access to this function.</h3>
        </div>
      )}
    </div>
  );
}

export default CutoffContent;
